import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Bookmark, ChevronDown, ChevronRight, Calendar } from 'lucide-react';
import { useChampionship } from '@/contexts/ChampionshipContext';
import { useTheme } from '@/contexts/ThemeContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SearchBar } from '@/components/SearchBar';
import volleyballApi from '@/services/api';

export const ChampionshipSelectorPage = () => {
  const navigate = useNavigate();
  const { selectChampionship, toggleFavorite, isFavorite, favorites } = useChampionship();
  const { isDark } = useTheme();
  const [championships, setChampionships] = useState([]);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [expandedTeams, setExpandedTeams] = useState({});

  useEffect(() => {
    loadChampionships();
  }, []);

  const loadChampionships = async () => {
    try {
      const data = await volleyballApi.getChampionships();
      
      // Check if data has the new format with categoryOrder
      if (data.championships && data.categoryOrder) {
        setChampionships(data.championships);
        setCategoryOrder(data.categoryOrder);
      } else {
        // Fallback for old format (just array of championships)
        setChampionships(data);
        setCategoryOrder([]);
      }
    } catch (error) {
      console.error('❌ Error loading championships:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChampionshipClick = (championship) => {
    selectChampionship(championship);
    navigate(`/championship/${championship.id}`);
  };

  const handleFavoriteClick = (e, championshipId) => {
    e.stopPropagation();
    toggleFavorite(championshipId);
  };

  const handleSearchChange = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }

    setSearching(true);
    try {
      const data = await volleyballApi.searchTeam(searchTerm);
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching:', error);
      setSearchResults(null);
    } finally {
      setSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchResults(null);
    setSearching(false);
  };

  const handleSearchResultClick = async (championshipId) => {
    try {
      const championship = await volleyballApi.getChampionship(championshipId);
      selectChampionship(championship);
      navigate(`/championship/${championshipId}`);
    } catch (error) {
      console.error('Error loading championship:', error);
    }
  };

  const toggleTeamExpand = (teamName) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamName]: !prev[teamName]
    }));
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  // Group championships by series + gender (e.g., "Serie C Maschile", "Serie D Femminile")
  // Use displaySeries (custom series if set) to match admin panel grouping
  const groupedChampionships = championships.reduce((acc, champ) => {
    const series = champ.displaySeries || champ.series;
    const gender = champ.gender || '';
    const key = `${series} ${gender}`.trim();
    
    if (!acc[key]) {
      acc[key] = {
        series: series,
        gender: gender,
        level: champ.level,
        championships: []
      };
    }
    acc[key].championships.push(champ);
    return acc;
  }, {});

  // Sort categories using admin-defined order or fallback to championship order
  const sortedCategories = Object.entries(groupedChampionships).sort((a, b) => {
    const categoryA = a[0];
    const categoryB = b[0];
    
    // If we have a custom category order from admin, use it
    if (categoryOrder.length > 0) {
      const indexA = categoryOrder.indexOf(categoryA);
      const indexB = categoryOrder.indexOf(categoryB);
      
      // If both categories are in the order list, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only A is in the list, A comes first
      if (indexA !== -1) return -1;
      // If only B is in the list, B comes first
      if (indexB !== -1) return 1;
    }
    
    // Fallback: sort by order field of first championship in each category
    const minOrderA = Math.min(...a[1].championships.map(c => c.order));
    const minOrderB = Math.min(...b[1].championships.map(c => c.order));
    return minOrderA - minOrderB;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Caricamento campionati...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-20">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Trophy className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  C.R. PUGLIA
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">2025/26</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Search Bar */}
        <div className="mb-6">
          <SearchBar 
            onSearch={handleSearchChange}
            onClear={handleClearSearch}
            placeholder="Cerca squadra..."
          />
        </div>

        {/* Search Results */}
        {searching && (
          <div className="mb-6 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Ricerca in corso...</p>
          </div>
        )}

        {searchResults && searchResults.results && searchResults.results.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Risultati ricerca "{searchResults.query}" ({searchResults.teamsCount} {searchResults.teamsCount === 1 ? 'squadra' : 'squadre'})
            </h3>
            <div className="space-y-2">
              {searchResults.results.map((team, idx) => {
                const isExpanded = expandedTeams[team.teamName];
                const champsCount = team.championships.length;
                
                return (
                  <div
                    key={`${team.teamName}-${idx}`}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    {/* Team Header */}
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div 
                          onClick={() => champsCount === 1 ? handleSearchResultClick(team.championships[0].id) : toggleTeamExpand(team.teamName)}
                          className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          <p className="font-semibold text-gray-900 dark:text-white truncate">
                            {team.teamName}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {champsCount === 1 
                              ? `${team.championships[0].series} ${team.championships[0].gender}${team.championships[0].group ? ` - Girone ${team.championships[0].group}` : ''}`
                              : `Gioca in ${champsCount} campionati`
                            }
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          {/* Favorite icon for single championship */}
                          {champsCount === 1 && (
                            <button
                              onClick={(e) => handleFavoriteClick(e, team.championships[0].id)}
                              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                              title={isFavorite(team.championships[0].id) ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
                            >
                              <Bookmark 
                                className={`h-5 w-5 ${
                                  isFavorite(team.championships[0].id)
                                    ? 'fill-pink-500 text-pink-500'
                                    : 'text-gray-400'
                                }`}
                              />
                            </button>
                          )}
                          {/* Expand/collapse icon */}
                          {champsCount > 1 ? (
                            <button
                              onClick={() => toggleTeamExpand(team.teamName)}
                              className="p-2"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-5 w-5 text-gray-400" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-gray-400" />
                              )}
                            </button>
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Championships Dropdown - Only show if multiple championships */}
                    {champsCount > 1 && isExpanded && (
                      <div className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                        {team.championships.map((championship, champIdx) => (
                          <div
                            key={`${championship.id}-${champIdx}`}
                            className="px-6 py-3 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border-b last:border-b-0 border-gray-200 dark:border-gray-600"
                          >
                            <div className="flex items-center justify-between">
                              <div 
                                onClick={() => handleSearchResultClick(championship.id)}
                                className="flex-1 cursor-pointer"
                              >
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {championship.series} {championship.gender}
                                </p>
                                {championship.group && (
                                  <p className="text-xs text-gray-500 dark:text-gray-300">
                                    Girone {championship.group}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {/* Favorite icon */}
                                <button
                                  onClick={(e) => handleFavoriteClick(e, championship.id)}
                                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg transition-colors"
                                  title={isFavorite(championship.id) ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
                                >
                                  <Bookmark 
                                    className={`h-4 w-4 ${
                                      isFavorite(championship.id)
                                        ? 'fill-pink-500 text-pink-500'
                                        : 'text-gray-400'
                                    }`}
                                  />
                                </button>
                                <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-300" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {searchResults && searchResults.results && searchResults.results.length === 0 && !searching && (
          <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Nessuna squadra trovata per "{searchResults.query}"
            </p>
          </div>
        )}

        {!searchResults && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Seleziona Campionato
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Tocca una categoria per vedere i gironi
              </p>
            </div>

            {/* Championships Accordion */}
            <div className="space-y-3">
              {sortedCategories.map(([categoryKey, categoryData]) => {
                const isExpanded = expandedCategories[categoryKey];
                const champsCount = categoryData.championships.length;
                
                return (
                  <div key={categoryKey} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {/* Category Header - Clickable */}
                    <button
                      onClick={() => toggleCategory(categoryKey)}
                      className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                          <Trophy className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="text-left">
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                            {categoryKey}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {champsCount} {champsCount === 1 ? 'girone' : 'gironi'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                          {categoryData.level}
                        </span>
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </button>

                    {/* Dropdown with Gironi */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                        {categoryData.championships
                          .sort((a, b) => a.group.localeCompare(b.group))
                          .map((championship) => (
                            <div
                              key={championship.id}
                              onClick={() => handleChampionshipClick(championship)}
                              className="px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-blue-600 dark:bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                  {championship.group}
                                </div>
                                <div>
                                  <span className="text-sm font-medium text-gray-700 dark:text-white">
                                    Girone {championship.group}
                                  </span>
                                  {!championship.hasData && (
                                    <div className="text-xs text-yellow-600 dark:text-yellow-400">
                                      ⚠ Nessun dato
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={(e) => handleFavoriteClick(e, championship.id)}
                                  className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
                                >
                                  <Bookmark
                                    className={`h-5 w-5 transition-colors ${
                                      isFavorite(championship.id)
                                        ? 'fill-pink-500 text-pink-500'
                                        : 'text-gray-400 dark:text-gray-300'
                                    }`}
                                  />
                                </button>
                                <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-300" />
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer Note */}
            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-center text-gray-700 dark:text-gray-300">
                <Bookmark className="inline h-4 w-4 mr-1 mb-1" />
                Tocca il segnalibro per aggiungere ai preferiti
              </p>
            </div>
          </>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 safe-area-inset-bottom">
        <div className="container mx-auto px-4">
          <div className="flex justify-around py-2">
            <button
              className="flex flex-col items-center py-2 px-4 text-blue-600 dark:text-blue-400"
            >
              <Trophy className="h-6 w-6" />
              <span className="text-xs mt-1 font-medium">Campionati</span>
            </button>
            <button
              onClick={() => navigate('/partite')}
              className="flex flex-col items-center py-2 px-4 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            >
              <Calendar className="h-6 w-6" />
              <span className="text-xs mt-1">Partite</span>
            </button>
            <button
              onClick={() => navigate('/preferiti')}
              className="flex flex-col items-center py-2 px-4 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 relative"
            >
              <Bookmark className="h-6 w-6" />
              <span className="text-xs mt-1">Preferiti</span>
              {favorites.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {favorites.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
